import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicBatch } from '../features/approvals/api.js';
import { ApprovalView } from '../features/approvals/approval-view.js';
import { pendingAttachmentsRefetchInterval } from '../features/approvals/pending-attachments.js';
import type { PublicApprovalBatch } from '../features/approvals/types.js';
import { fetchPublicCalendar, publicCalendarQueryKey } from '../features/client-calendar/api.js';
import {
  ClientCalendarView,
  monthFromLabel,
} from '../features/client-calendar/client-calendar-view.js';

/**
 * The hotel's calendar at the top of the approval page (2026-09-29): opened
 * with the same batch token, on the batch's own month, with the client's
 * whole history behind every post. A calendar that fails to load must not
 * take the approval list with it — it degrades to one line.
 */
function EmbeddedCalendar({
  token,
  initial,
}: {
  token: string;
  initial: { year: number; month: number } | null;
}) {
  const [period, setPeriod] = useState<{ year: number; month: number } | null>(null);
  const shown = period ?? initial;
  const calendar = useQuery({
    queryKey: publicCalendarQueryKey(token, shown?.year, shown?.month),
    queryFn: () => fetchPublicCalendar(token, shown?.year, shown?.month),
    retry: false,
  });

  if (calendar.isLoading) {
    return <p className="text-body text-[var(--fg-2)]">A naptár betöltése…</p>;
  }
  if (calendar.isError || !calendar.data) {
    return (
      <p className="text-body text-[var(--fg-2)]" data-testid="calendar-unavailable">
        A naptár most nem érhető el. A jóváhagyandó anyagok lentebb ugyanúgy elérhetők.
      </p>
    );
  }
  return (
    <ClientCalendarView
      data={calendar.data}
      token={token}
      embedded
      onMonthChange={(year, month) => setPeriod({ year, month })}
    />
  );
}

function ApprovePage() {
  const { token } = Route.useParams();
  const batch = useQuery<PublicApprovalBatch>({
    queryKey: ['public-approval', token],
    queryFn: () => fetchPublicBatch(token),
    retry: false,
    refetchInterval: (q) => pendingAttachmentsRefetchInterval(q.state.data),
  });

  if (batch.isLoading) {
    return (
      <main className="min-h-screen bg-[var(--bg)] p-8">
        <p className="text-center text-[var(--fg-2)]">Betöltés…</p>
      </main>
    );
  }

  if (batch.isError || !batch.data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[var(--bg)] p-6">
        <div className="max-w-md rounded-lg border border-[var(--app-divider)] bg-[var(--n-0)] p-8 text-center shadow-1">
          <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--st-rejected-bg)] text-[var(--st-rejected-fg)]">
            <svg
              width={20}
              height={20}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h1 className="text-h2 font-semibold text-[var(--ink-700)]">
            Érvénytelen vagy lejárt link
          </h1>
          <p className="mt-2 text-body text-[var(--fg-2)]">
            A jóváhagyási link nem érvényes, lejárt vagy vissza lett vonva. Kérj új linket a
            kampányfelelőstől.
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] pt-8">
      <ApprovalView
        batch={batch.data}
        token={token}
        batchId={null}
        calendar={
          <EmbeddedCalendar token={token} initial={monthFromLabel(batch.data.monthLabel)} />
        }
      />
    </div>
  );
}

export const Route = createFileRoute('/approve/$token')({
  component: ApprovePage,
});
